import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { retrieveProfileData, updateMoneyChitStatus, updateInstallmentMoneyChitPayment, updateMoneyChitSettlement } from "../../api/PupilApi";

// UpdateStatusForm component (keep the same)
function UpdateStatusForm({ plan, onSave, onCancel }) {
  const [status, setStatus] = useState(plan.planStatus || "pending");

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm">
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <div className="flex-grow w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">Update Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex-grow sm:flex-grow-0"
            onClick={() => onSave(status)}
          >
            💾 Save
          </button>
          <button
            className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex-grow sm:flex-grow-0"
            onClick={onCancel}
          >
            ✕ Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// WithdrawalStatusForm component (keep the same)
function WithdrawalStatusForm({ plan, onSave, onCancel }) {
  const [withdrawalData, setWithdrawalData] = useState({
    status: plan.withdrawalStatus,
    withdrawalDate: new Date().toISOString().split('T')[0],
    notes: '',
    paidAmount: '',
    proofLink: plan.proofLink || ''
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'paidAmount') {
      const amount = parseFloat(value) || 0;
      if (amount > plan.chitValue) {
        setError(`Settlement amount cannot exceed chit value of ₹${plan.chitValue}`);
      } else {
        setError('');
      }
    }
    
    setWithdrawalData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (withdrawalData.paidAmount && parseFloat(withdrawalData.paidAmount) > plan.chitValue) {
      setError(`Settlement amount cannot exceed chit value of ₹${plan.chitValue}`);
      return;
    }
    setError('');
    onSave(withdrawalData);
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl border border-orange-100 shadow-sm mt-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
        <h4 className="font-semibold text-lg text-gray-800">Update Settlement Status</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            name="status"
            value={withdrawalData.status}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          >
            <option value="fullSettlement">Full Settlement</option>
            <option value="partialSettlement">Partially settled</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Date</label>
          <input
            type="date"
            name="withdrawalDate"
            value={withdrawalData.withdrawalDate}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Amount (Max: ₹{plan.chitValue})</label>
          <input
            type="number"
            name="paidAmount"
            value={withdrawalData.paidAmount}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Settlement Amount"
            max={plan.chitValue}
          />
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <input
            type="text"
            name="notes"
            value={withdrawalData.notes}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Additional notes..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Proof Drive Link</label>
          <input
            type="text"
            name="proofLink"
            value={withdrawalData.proofLink}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Link to proof of payment..."
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-medium flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={!!error}
        >
          💾 Save Withdrawal
        </button>
        <button
          className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-4 py-2 rounded-lg font-medium flex-1"
          onClick={onCancel}
        >
          ✕ Cancel
        </button>
      </div>
    </div>
  );
}

// PaymentEditForm component (keep the same)
function PaymentEditForm({ installment, onSave, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    installmentNumber: installment.installmentNumber,
    paidAmount: installment.paidAmount || installment.installmentFullChitBiderAmount || 0,
    modeOfTransaction: installment.modeOfTransaction || 'cash',
    paidDate: installment.paidDate || '',
    transactionId: installment.transactionId || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving payment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-200 shadow-lg mt-4" onSubmit={handleSubmit}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></div>
        <h4 className="font-semibold text-lg text-gray-800">Edit Installment #{installment.installmentNumber}</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Paid Amount</label>
          <input
            name="paidAmount"
            type="number"
            min="0"
            value={formData.paidAmount}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Payment Method</label>
          <select
            name="modeOfTransaction"
            value={formData.modeOfTransaction}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          >
            <option value="cash">Cash</option>
            <option value="accountpay">Account Pay</option>
            <option value="creditcard">Credit Card</option>
            <option value="wallet">Wallet</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Paid Date</label>
          <input
            name="paidDate"
            type="date"
            value={formData.paidDate}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Transaction ID</label>
          <input
            name="transactionId"
            type="text"
            value={formData.transactionId}
            onChange={handleChange}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
            required
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button
          type="submit"
          disabled={loading}
          className={`bg-gradient-to-r from-green-500 to-green-600 
                      hover:from-green-600 hover:to-green-700 
                      text-white px-6 py-3 rounded-xl font-semibold 
                      shadow-md hover:shadow-lg transition-all duration-200 flex-grow
                      ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {loading ? "⏳ Saving..." : "💳 Save Payment"}
        </button>
        <button
          type="button"
          className="bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow"
          onClick={onCancel}
        >
          ↶ Cancel
        </button>
      </div>
    </form>
  );
}

// NEW: ChitMemberList component to display chit members
function ChitMemberList({ members, planId }) {
  if (!members || members.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-4 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"></div>
        <h4 className="font-semibold text-gray-800">Chit Members</h4>
        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
          {members.length} Member{members.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {members.map((member, index) => (
          <div key={index} className="bg-white p-3 rounded-lg border border-purple-100">
            <div className="space-y-2">
              <div>
                <div className="text-xs text-gray-500">Full Name</div>
                <div className="font-medium text-gray-800">{member.fullName || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Email</div>
                <div className="font-medium text-gray-800 truncate">{member.pupilEmail || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Mobile</div>
                <div className="font-medium text-gray-800">{member.mobileNumber || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Aadhar</div>
                <div className="font-medium text-gray-800">{member.aadhar || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">PAN</div>
                <div className="font-medium text-gray-800">{member.pan || 'N/A'}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ProfileViewMoneyChit = () => {
  const { email } = useParams();
  const [searchParams] = useSearchParams();
  const chitId = searchParams.get('chitId');
  const chitTitle = searchParams.get('chitTitle');
  const chitValue = searchParams.get('chitValue');
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editPlanId, setEditPlanId] = useState(null);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [editInstallment, setEditInstallment] = useState(null);
  const [withdrawStatus, setWithdrawStatus] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!email) return;
    
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const res = await retrieveProfileData(email);
        setProfile(res.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch profile data.");
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [email]);

  // ENHANCED: Process chit data to properly separate by planId and include chitMemberList
  const processChitPlans = (profileData) => {
    if (!profileData) return [];

    // If we have chitPlanDetails array, use that
    if (profileData.chitPlanDetails && Array.isArray(profileData.chitPlanDetails)) {
      return profileData.chitPlanDetails;
    }

    // If we have chitData object (from your JSON structure), convert it to array format
    if (profileData.chitData) {
      const chitData = profileData.chitData;
      return [{
        planId: chitData.planId,
        chitTitle: chitData.chitTitle,
        chitValue: chitData.chitValue,
        startDate: chitData.startDate,
        endDate: chitData.endDate,
        months: chitData.months,
        members: chitData.members,
        monthlyDecrease: chitData.monthlyDecrease,
        fullChitBidderAmount: chitData.fullChitBidderAmount,
        startFullChitNonBidder: chitData.startFullChitNonBidder,
        scheduleData: chitData.scheduleData,
        chitMemberList: chitData.chitMemberList,
        // Add other necessary fields with default values
        planStatus: "pending",
        withdrawalStatus: null,
        chitInstallmentDetailsList: [],
        settlements: []
      }];
    }

    return [];
  };

  // Get processed chit plans
  const allChitPlans = processChitPlans(profile);

  // Filter chit plans by chit value if specified
  const filteredChitPlans = allChitPlans.filter(plan => {
    if (!chitValue) return true;
    const planChitValue = plan.chitValue;
    const targetChitValue = parseInt(chitValue);
    return planChitValue === targetChitValue;
  });

  // Group chit plans by planId to handle duplicates
  const groupedChitPlans = filteredChitPlans.reduce((acc, plan) => {
    const planId = plan.planId;
    if (!acc[planId]) {
      acc[planId] = {
        ...plan,
        // If there are multiple entries with same planId, we'll merge chitMemberList
        chitMemberList: plan.chitMemberList || []
      };
    } else {
      // Merge chit member lists if same planId appears multiple times
      if (plan.chitMemberList && Array.isArray(plan.chitMemberList)) {
        acc[planId].chitMemberList = [...acc[planId].chitMemberList, ...plan.chitMemberList];
      }
    }
    return acc;
  }, {});

  // Convert back to array for rendering
  const uniqueChitPlans = Object.values(groupedChitPlans);

  // Debug logging
  useEffect(() => {
    if (profile) {
      console.log('All chit plans:', allChitPlans);
      console.log('Filtered chit plans:', filteredChitPlans);
      console.log('Grouped chit plans:', groupedChitPlans);
      console.log('Unique chit plans:', uniqueChitPlans);
    }
  }, [profile, allChitPlans, filteredChitPlans, groupedChitPlans, uniqueChitPlans]);

  const handleStatusUpdate = async (planId, newStatus) => {
    try {
      setUpdatingStatus(true);
      await updateMoneyChitStatus(email, planId, newStatus);
      
      setProfile((prev) => ({
        ...prev,
        chitPlanDetails: prev.chitPlanDetails.map((p) =>
          p.planId === planId ? { ...p, planStatus: newStatus } : p
        ),
      }));
      
      setEditPlanId(null);
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentUpdate = async (planId, installmentData) => {
    try {
      const paymentRequest = {
        email,
        planId,
        installmentNumber: installmentData.installmentNumber,
        paidAmount: installmentData.paidAmount,
        paidFrom: installmentData.modeOfTransaction,
        paidDate: installmentData.paidDate,
        transactionId: installmentData.transactionId,
      };
      
      await updateInstallmentMoneyChitPayment(paymentRequest);
      const res = await retrieveProfileData(email);
      setProfile(res.data);
      setEditInstallment(null);
      setSelectedPlanForPayment(null);
    } catch (err) {
      alert("Failed to update payment");
    }
  };

  const handleSettlmentUpdate = async (planId, withdrawalData) => {
    try {
      const withdrawalRequest = {
        status: withdrawalData.status,
        withdrawalDate: withdrawalData.withdrawalDate,
        notes: withdrawalData.notes,
        paidAmount: parseFloat(withdrawalData.paidAmount),
        proofLink: withdrawalData.proofLink,
        planId: planId,
        email: email
      };

      await updateMoneyChitSettlement(withdrawalRequest);
      const res = await retrieveProfileData(email);
      setProfile(res.data);
      setWithdrawStatus(null);
    } catch (err) {
      console.error('Error updating withdrawal:', err);
      alert('Failed to update withdrawal status');
    }
  };

  // Helper function to check if status is approved
  const isStatusApproved = (plan) => {
    const status = plan.planStatus || '';
    return status.toLowerCase() === 'approved';
  };

  // Helper function to check if status is pending or needs approval
  const isStatusPending = (plan) => {
    const status = plan.planStatus || '';
    return status.toLowerCase() === 'pending' || status.toLowerCase() === 'requested' || !status;
  };

  // Helper function to check if settlement is completed
  const isSettlementCompleted = (plan) => {
    const withdrawalStatus = plan.withdrawalStatus || '';
    return withdrawalStatus.toLowerCase() === 'fullsettlement' || 
           withdrawalStatus.toLowerCase() === 'partialsettlement' ||
           withdrawalStatus.toLowerCase() === 'completed';
  };

  // Helper function to get withdrawal status badge
  const getWithdrawalStatusBadge = (status) => {
    const statusConfig = {
      'requested': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'approved': 'bg-blue-100 text-blue-800 border border-blue-200',
      'completed': 'bg-green-100 text-green-800 border border-green-200',
      'rejected': 'bg-red-100 text-red-800 border border-red-200',
      'cancelled': 'bg-gray-100 text-gray-800 border border-gray-200',
      'fullSettlement': 'bg-green-100 text-green-800 border border-green-200',
      'partialSettlement': 'bg-blue-100 text-blue-800 border border-blue-200'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig[status] || 'bg-gray-100'}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Function to display settlement details
  const renderSettlementDetails = (plan) => {
    if (!plan.settlements || plan.settlements.length === 0) return null;

    const settlement = plan.settlements[0];
    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
          <h4 className="font-semibold text-gray-800">Settlement Details</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="text-sm text-gray-600">Settlement Date</div>
            <div className="font-medium text-gray-800">{settlement.withdrawalDate}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Settlement Amount</div>
            <div className="font-medium text-green-600">₹{settlement.paidAmount}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Processed By</div>
            <div className="font-medium text-gray-800">{settlement.processedBy}</div>
          </div>
        </div>
        {settlement.notes && (
          <div className="mt-2">
            <div className="text-sm text-gray-600">Notes</div>
            <div className="font-medium text-gray-800">{settlement.notes}</div>
          </div>
        )}
      </div>
    );
  };

  // Get chit value display name
  const getChitValueDisplay = (value) => {
    if (value >= 500000) return "5 Lakhs Chit";
    if (value >= 100000) return "1 Lakh Chit";
    return `${value} Chit`;
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Loading profile data...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center">
      <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-red-200 max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">⚠️</span>
        </div>
        <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl shadow-2xl overflow-hidden mb-8">
          <div className="p-6 sm:p-8 text-white">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl lg:text-4xl font-bold mb-1">{profile.name || "Profile"}</h1>
                <p className="text-blue-100 text-lg">Chit Plan Management</p>
                {chitValue && (
                  <div className="mt-2">
                    <p className="text-blue-200 text-sm">
                      Viewing: <span className="font-semibold">{getChitValueDisplay(parseInt(chitValue))}</span>
                    </p>
                    {chitTitle && (
                      <p className="text-blue-200 text-xs">Chit Title: {chitTitle}</p>
                    )}
                    <p className="text-blue-200 text-xs mt-1">
                      Showing {uniqueChitPlans.length} plan{uniqueChitPlans.length !== 1 ? 's' : ''} for this chit type
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl w-full sm:w-auto text-center"
                >
                  ← Back to Dashboard
                </button>
                <button
                  onClick={() => navigate('/money-chit-dashboard')}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl w-full sm:w-auto text-center"
                >
                  📊 All Chits
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-800">Profile Information</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
              <div className="text-sm text-gray-600 font-medium">Email</div>
              <div className="text-lg font-semibold text-gray-800 truncate">{profile.pupilEmail}</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100">
              <div className="text-sm text-gray-600 font-medium">Mobile</div>
              <div className="text-lg font-semibold text-gray-800">{profile.pupilMobile}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-100">
              <div className="text-sm text-gray-600 font-medium">Status</div>
              <div className="text-lg font-semibold text-gray-800 capitalize">{profile.status}</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl border border-orange-100">
              <div className="text-sm text-gray-600 font-medium">Role</div>
              <div className="text-lg font-semibold text-gray-800 capitalize">{profile.role}</div>
            </div>
          </div>
        </div>

        {/* Chit Plans Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="w-3 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-800">
              {uniqueChitPlans.length === 1 ? 'Chit Plan Details' : 'Chit Plans'}
            </h2>
            <span className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              {uniqueChitPlans.length} Plan{uniqueChitPlans.length !== 1 ? 's' : ''}
            </span>
            {chitValue && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                parseInt(chitValue) === 100000 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                  : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
              }`}>
                {getChitValueDisplay(parseInt(chitValue))}
              </span>
            )}
          </div>

          {uniqueChitPlans.length > 0 ? (
            <div className="grid gap-6">
              {uniqueChitPlans.map((plan) => {
                const isApproved = isStatusApproved(plan);
                const isPending = isStatusPending(plan);
                const hasSettlement = plan.settlements && plan.settlements.length > 0;
                const isSettled = isSettlementCompleted(plan);
                const chitDisplayName = getChitValueDisplay(plan.chitValue);
                
                return (
                  <div key={plan.planId} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 hover:border-blue-300 transition-all duration-300 shadow-sm hover:shadow-md">
                    {/* Plan Header */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{plan.chitTitle || chitDisplayName}</h3>
                          <p className="text-gray-600">Plan ID: {plan.planId} • {chitDisplayName}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-start lg:items-end gap-2 mt-2 lg:mt-0">
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${
                          isApproved ? 'bg-green-100 text-green-800 border border-green-200' : 
                          isPending ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 
                          'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {plan.planStatus || "-"}
                        </span>
                        {plan.withdrawalStatus && (
                          <div className="text-sm">
                            <span className="text-gray-600 mr-2">Withdrawal:</span>
                            {getWithdrawalStatusBadge(plan.withdrawalStatus)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Plan Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Chit Value</div>
                        <div className="text-lg font-bold text-blue-600">₹{plan.chitValue?.toLocaleString()}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Duration</div>
                        <div className="text-lg font-bold text-green-600">{plan.months} Months</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Start Date</div>
                        <div className="text-lg font-bold text-purple-600">{plan.startDate}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Members</div>
                        <div className="text-lg font-bold text-orange-600">{plan.members || (plan.chitMemberList ? plan.chitMemberList.length : 0)}</div>
                      </div>
                    </div>

                    {/* Additional Plan Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Monthly Amount</div>
                        <div className="text-lg font-bold text-indigo-600">₹{plan.fullChitBidderAmount || plan.startFullChitNonBidder}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">Monthly Decrease</div>
                        <div className="text-lg font-bold text-red-600">₹{plan.monthlyDecrease}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">End Date</div>
                        <div className="text-lg font-bold text-purple-600">{plan.endDate}</div>
                      </div>
                    </div>

                    {/* Chit Members List */}
                    <ChitMemberList members={plan.chitMemberList} planId={plan.planId} />

                    {/* Settlement Details */}
                    {hasSettlement && renderSettlementDetails(plan)}

                    {/* Action Buttons */}
                    {editPlanId === plan.planId ? (
                      <UpdateStatusForm
                        plan={plan}
                        onSave={(newStatus) => handleStatusUpdate(plan.planId, newStatus)}
                        onCancel={() => setEditPlanId(null)}
                      />
                    ) : withdrawStatus === plan.planId ? (
                      <WithdrawalStatusForm
                        plan={plan}
                        onSave={(withdrawalData) => handleSettlmentUpdate(plan.planId, withdrawalData)}
                        onCancel={() => setWithdrawStatus(null)}
                      />
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-3">
                        {/* Show Update Status button only for pending plans */}
                        {isPending && (
                          <button 
                            disabled={updatingStatus}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setEditPlanId(plan.planId)}
                          >
                            {updatingStatus ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Updating...
                              </>
                            ) : (
                              <>
                                <span>⚙️</span> Update Status
                              </>
                            )}
                          </button>
                        )}
                        
                        {/* Show Manage Payments and Update Settlement buttons only for approved plans that are NOT settled */}
                        {isApproved && !isSettled && (
                          <>
                            {plan.chitInstallmentDetailsList && plan.chitInstallmentDetailsList.length > 0 && (
                              <button 
                                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2"
                                onClick={() => setSelectedPlanForPayment(plan)}
                              >
                                <span>💳</span> Manage Payments
                              </button>
                            )}
                            
                            <button 
                              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2"
                              onClick={() => setWithdrawStatus(plan.planId)}
                            >
                              <span>💳</span> {hasSettlement ? 'Update Settlement' : 'Withdraw Chit Status'}
                            </button>
                          </>
                        )}
                        
                        {/* Show only Manage Payments button for approved plans that ARE settled */}
                        {isApproved && isSettled && (
                          <>
                            {plan.chitInstallmentDetailsList && plan.chitInstallmentDetailsList.length > 0 && (
                              <button 
                                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex-grow flex items-center justify-center gap-2"
                                onClick={() => setSelectedPlanForPayment(plan)}
                              >
                                <span>💳</span> Manage Payments
                              </button>
                            )}
                          </>
                        )}
                        
                        {/* Show no actions for other statuses */}
                        {!isPending && !isApproved && (
                          <div className="text-center py-3 text-gray-500 flex-grow">
                            Plan status: {plan.planStatus}. No actions available.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {chitValue ? `No Matching Chit Plan Found` : `No Chit Plans Found`}
              </h3>
              <p className="text-gray-500 mb-4">
                {chitValue 
                  ? `No chit plan found matching ${getChitValueDisplay(parseInt(chitValue))} for this user.`
                  : "This profile doesn't have any chit plans yet."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/money-chit-dashboard')}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                >
                  ← Back to Chit Dashboard
                </button>
                {chitValue && (
                  <button
                    onClick={() => {
                      // Clear the filter to show all chit plans
                      navigate(location.pathname);
                    }}
                    className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                  >
                    View All Chit Plans
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Installment Modal (keep the same) */}
        {selectedPlanForPayment && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-green-500 to-teal-600 p-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold">Payment Management</h3>
                    <p className="text-green-100">
                      {selectedPlanForPayment.chitTitle || getChitValueDisplay(selectedPlanForPayment.chitValue)}
                    </p>
                    <p className="text-green-200 text-sm">Plan ID: {selectedPlanForPayment.planId}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedPlanForPayment(null);
                      setEditInstallment(null);
                    }}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all duration-200"
                  >
                    <span className="text-xl">✕</span>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="overflow-y-auto flex-grow p-4 sm:p-6">
                {selectedPlanForPayment.chitInstallmentDetailsList && selectedPlanForPayment.chitInstallmentDetailsList.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden lg:block">
                      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                              <th className="p-4 text-left font-semibold text-gray-700">#</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Due Date</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Non-Bidder Amount</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Bidder Amount</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Status</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Paid Date</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Paid By</th>
                              <th className="p-4 text-left font-semibold text-gray-700">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedPlanForPayment.chitInstallmentDetailsList.map((inst, idx) => (
                              <React.Fragment key={idx}>
                                <tr className={`hover:bg-gray-50 transition-colors ${editInstallment?.installmentNumber === inst.installmentNumber ? "bg-blue-50" : ""}`}>
                                  <td className="p-4 font-medium text-gray-900">{inst.installmentNumber}</td>
                                  <td className="p-4 text-gray-700">{inst.installmentDate || '-'}</td>
                                  <td className="p-4 font-semibold text-green-600">₹{inst.installmentFullChitNonBiderAmount || 0}</td>
                                  <td className="p-4 font-semibold text-blue-600">₹{inst.installmentFullChitBiderAmount || 0}</td>
                                  <td className="p-4">
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${inst.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                      {inst.paymentStatus || inst.installmentStatus || 'Pending'}
                                    </span>
                                  </td>
                                  <td className="p-4 text-gray-700">{inst.paidDate || '-'}</td>
                                  <td className="p-4 text-gray-700">{inst.updatedBy || '-'}</td>
                                  <td className="p-4">
                                    <button
                                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${editInstallment?.installmentNumber === inst.installmentNumber ? "bg-red-500 hover:bg-red-600 text-white" : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"}`}
                                      onClick={() => setEditInstallment(editInstallment?.installmentNumber === inst.installmentNumber ? null : inst)}
                                    >
                                      {editInstallment?.installmentNumber === inst.installmentNumber ? "Cancel" : "Edit"}
                                    </button>
                                  </td>
                                </tr>
                                {editInstallment?.installmentNumber === inst.installmentNumber && (
                                  <tr className="bg-blue-50">
                                    <td colSpan="8" className="p-4">
                                      <PaymentEditForm
                                        installment={editInstallment}
                                        onSave={(formData) => handlePaymentUpdate(selectedPlanForPayment.planId, formData)}
                                        onCancel={() => setEditInstallment(null)}
                                      />
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Cards */}
                    <div className="lg:hidden space-y-4">
                      {selectedPlanForPayment.chitInstallmentDetailsList.map((inst, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-5 border border-gray-200">
                          <div className="flex flex-col gap-2 mb-4">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold text-gray-800">Installment #{inst.installmentNumber}</span>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${inst.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {inst.paymentStatus || inst.installmentStatus || 'Pending'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">Due Date: {inst.installmentDate || '-'}</div>
                            <div className="text-sm text-gray-600">Paid By: <span className="font-medium text-gray-800">{inst.updatedBy || "-"}</span></div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">Full Amount</div>
                              <div className="font-bold text-green-600">₹{inst.installmentFullChitBiderAmount || 0}</div>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">Half Amount</div>
                              <div className="font-bold text-blue-600">₹{inst.installmentHalfChitBiderAmount || 0}</div>
                            </div>
                            <div className="bg-white p-3 rounded-xl border border-gray-200">
                              <div className="text-xs text-gray-600">Paid Date</div>
                              <div className="font-medium text-gray-800">{inst.paidDate || '-'}</div>
                            </div>
                          </div>

                          <button
                            className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 ${editInstallment?.installmentNumber === inst.installmentNumber ? "bg-red-500 hover:bg-red-600 text-white" : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"}`}
                            onClick={() => setEditInstallment(editInstallment?.installmentNumber === inst.installmentNumber ? null : inst)}
                          >
                            {editInstallment?.installmentNumber === inst.installmentNumber ? "✕ Cancel Edit" : "✏️ Edit Payment"}
                          </button>

                          {editInstallment?.installmentNumber === inst.installmentNumber && (
                            <div className="mt-4">
                              <PaymentEditForm
                                installment={editInstallment}
                                onSave={(formData) => handlePaymentUpdate(selectedPlanForPayment.planId, formData)}
                                onCancel={() => setEditInstallment(null)}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">💸</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Installments Found</h3>
                    <p className="text-gray-500">This plan doesn't have any installment data yet.</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  onClick={() => {
                    setSelectedPlanForPayment(null);
                    setEditInstallment(null);
                  }}
                >
                  Close Payment Manager
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileViewMoneyChit;